import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { IconButton } from '@/components/IconButton'
import { Icon } from '@/components/Icon'
import { Note } from '@/components/Note'
import { Pager } from '@/components/Pager'
import { GridTable } from '@/components/GridTable'
import { useCursorPager } from '@/data/useCursorPager'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type Connection = components['schemas']['Connection']
type WorkspaceSummary = components['schemas']['WorkspaceSummary']
type ConnectionKind = components['schemas']['ConnectionKind']

const STATUS_LABEL: Record<string, string> = { ACTIVATION_PENDING: 'Awaiting activation', ACTIVE: 'Active', DEACTIVATED: 'Deactivated' }
const STATUS_CHIP: Record<string, 'pending' | 'active' | 'off'> = { ACTIVATION_PENDING: 'pending', ACTIVE: 'active', DEACTIVATED: 'off' }
const KIND_LABEL: Record<ConnectionKind, string> = { STANDARD: 'Standard', DISASTER_RECOVERY: 'Disaster recovery (standby)' }
const KIND_CHIP: Record<ConnectionKind, 'default' | 'warning'> = { STANDARD: 'default', DISASTER_RECOVERY: 'warning' }

/**
 * `v-connected` — process/09-SCREEN-SPECS/v-connected.md. **No connection carries a stored name**
 * (§13.12.6, rule 1) — each end is rendered from `initiator.name`/`authenticator.name`, resolved
 * server-side per region, never composed or cached under a name this screen invents.
 *
 * **`initiated_by`/`activated_by`/`deactivated_by` are bare user ids, with no `display_name`**
 * (unlike `BackupJob.triggered_by`, which carries one) — reported as a gap rather than papered
 * over with a name this screen has no way to resolve.
 *
 * **One of the spec's own three `grid-tools` `icon-button`s added W157** — a real "Refresh"
 * invalidating the connections query. No `data-act` — the Actions table names only `Add
 * connection`/`Activate`/`Deactivate`. The other two are not built: no filter state exists to
 * clear and no column-visibility state exists to back a chooser.
 *
 * **`segmented-control`/`segment-option`/`grid-filter-row` added W168, both real but scoped to the
 * loaded page only — `workspaceListConnections` has no `kind` or text filter parameter at all,
 * only `cursor`/`page_size`.** Filtering client-side over rows already on the screen is honest (it
 * never claims to search rows not yet fetched, and this list is not expected to run to many
 * pages); filtering server-side is not possible without a parameter the contract does not have.
 * **`avatar` remains unbuilt** — the already-reported gap above (bare user ids, no `display_name`)
 * is the reason, not an oversight.
 *
 * **Both chips recoloured against the prototype's own `v-connected` markup (W172)**: `status` was
 * flat grey for every value; it is now `active`/`pending`/`off` matching `Active`/`Activation
 * pending`/`Deactivated`'s own real `st-active`/`st-pending`/`st-off` classes. `kind` was inverted
 * — `DISASTER_RECOVERY` rendered the default blue chip and `STANDARD` rendered plain grey, when
 * the prototype draws a plain `Connection` row as the bare default chip and `Disaster recovery` as
 * `solid-warn`.
 *
 * **`Filter by status` added, client-side, same basis as `kindFilter` above** — `status` is
 * already fetched on every row and rendered as a chip; filtering the loaded page by it needs no
 * new parameter. **The panel-header's own leading `icon` (`i-inout`, teal) restored** — present in
 * `Prototype_v2.html`'s own markup beside "Connections", found absent by a visual-diff sweep.
 */
export function VConnected() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()
  const pager = useCursorPager()
  const [showAdd, setShowAdd] = useState(false)
  const [targetWorkspaceId, setTargetWorkspaceId] = useState('')
  const [kind, setKind] = useState<ConnectionKind>('STANDARD')
  const [kindFilter, setKindFilter] = useState<ConnectionKind | ''>('')
  const [statusFilter, setStatusFilter] = useState<Connection['status'] | ''>('')
  const [query, setQuery] = useState('')

  const workspaces = useQuery({
    queryKey: ['workspaces', orgId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items ?? []) as unknown as readonly WorkspaceSummary[]
    },
  })

  const connections = useQuery({
    queryKey: ['connections', orgId, workspaceId, pager.cursor],
    enabled: Boolean(orgId && workspaceId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/connections', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { cursor: pager.cursor } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const create = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/connections', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        body: { authenticator_workspace_id: targetWorkspaceId, kind },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      return unwrap(response as { data?: Connection; error?: Problem })
    },
    onSuccess: () => {
      setShowAdd(false)
      setTargetWorkspaceId('')
      queryClient.invalidateQueries({ queryKey: ['connections', orgId, workspaceId] })
    },
  })

  const activate = useMutation({
    mutationFn: async (connectionId: string) => {
      const client = getApiClient()
      const { error } = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/connections/{connection_id}/activate', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, connection_id: connectionId } },
      })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['connections', orgId, workspaceId] }),
  })

  const deactivate = useMutation({
    mutationFn: async (connectionId: string) => {
      const client = getApiClient()
      const { error } = await client.DELETE('/orgs/{org_id}/workspaces/{workspace_id}/connections/{connection_id}', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, connection_id: connectionId } },
      })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['connections', orgId, workspaceId] }),
  })

  const problem = connections.error instanceof ApiProblemError ? connections.error.problem : undefined
  const allRows = (connections.data?.items ?? []) as unknown as readonly Connection[]
  const rows = allRows.filter(
    (c) =>
      (!kindFilter || c.kind === kindFilter) &&
      (!statusFilter || c.status === statusFilter) &&
      (!query || `${c.initiator.name} ${c.authenticator.name}`.toLowerCase().includes(query.toLowerCase())),
  )
  const otherWorkspaces = (workspaces.data ?? []).filter((w) => w.workspace_id !== workspaceId)

  return (
    <div data-scr={unitId('v-connected')}>
      <div data-cmp="panel-header" className="flex items-center gap-5 mb-6">
        <Icon name="i-inout" size="s" color="teal" />
        <h1 className="m-0 text-section font-semibold flex-1">Connections</h1>
        <div data-cmp="grid-tools">
          <IconButton
            data-act={undefined}
            icon="i-refresh"
            label="Refresh"
            disabled={connections.isFetching}
            onClick={() => queryClient.invalidateQueries({ queryKey: ['connections', orgId, workspaceId] })}
          />
          <Button data-act={actionId('connection.create')} onClick={() => setShowAdd((v) => !v)}>
            Add connection
          </Button>
        </div>
      </div>

      <Note>
        Connect this workspace to other Bitbucket Cloud workspaces to restore into them and migrate repositories between them. A
        connection must be activated at both ends.
      </Note>
      <Note>
        <b>Two types, one list.</b> A <b>Connection</b> is for everyday restore into a second workspace and migration between
        workspaces. A <b>Disaster recovery</b> connection pulls data out when the source workspace is inaccessible — use it only
        then, and there is one per workspace.
      </Note>

      {showAdd && (
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6 my-6" style={{ maxWidth: 480 }}>
          <label className="block mb-4 text-small font-semibold text-tx2">
            Authenticating workspace
            <select value={targetWorkspaceId} onChange={(e) => setTargetWorkspaceId(e.target.value)} className="block w-full mt-2 border border-bd2 rounded-1 py-3 px-4 text-body-lg">
              <option value="">Choose a workspace</option>
              {otherWorkspaces.map((w) => (
                <option key={w.workspace_id} value={w.workspace_id}>
                  {w.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block mb-5 text-small font-semibold text-tx2">
            Kind
            <select value={kind} onChange={(e) => setKind(e.target.value as ConnectionKind)} className="block w-full mt-2 border border-bd2 rounded-1 py-3 px-4 text-body-lg">
              <option value="STANDARD">{KIND_LABEL.STANDARD}</option>
              <option value="DISASTER_RECOVERY">{KIND_LABEL.DISASTER_RECOVERY}</option>
            </select>
          </label>
          {create.error instanceof ApiProblemError && <Note>{create.error.problem.title}</Note>}
          <Button data-act={actionId('connection.create')} disabled={!targetWorkspaceId || create.isPending} onClick={() => create.mutate()}>
            Propose connection
          </Button>
        </div>
      )}

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      <div data-cmp="segmented-control" className="flex gap-2 mt-6">
        {([{ label: 'All', value: '' as const }, { label: 'Connections', value: 'STANDARD' as const }, { label: 'Disaster recovery', value: 'DISASTER_RECOVERY' as const }]).map((f) => (
          <button
            key={f.label}
            type="button"
            data-cmp="segment-option"
            onClick={() => setKindFilter(f.value)}
            className={`py-2 px-4 rounded-1 text-small font-semibold ${kindFilter === f.value ? 'bg-blue text-white' : 'bg-row text-tx2'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div data-cmp="grid-filter-row" className="mt-4 flex items-center gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by workspace"
          className="border border-bd2 rounded-1 py-2 px-3 text-small"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as Connection['status'] | '')}
          className="border border-bd2 rounded-1 py-2 px-3 text-small"
        >
          <option value="">Filter by status</option>
          {Object.entries(STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <Button
          data-act={undefined}
          ghost
          onClick={() => {
            setKindFilter('')
            setStatusFilter('')
            setQuery('')
          }}
        >
          Reset filters
        </Button>
      </div>

      <div className="mt-4">
        <GridTable
          columns={[
            { key: 'type', header: 'Type', render: (c) => <Chip variant={KIND_CHIP[c.kind]}>{KIND_LABEL[c.kind]}</Chip> },
            { key: 'initiator', header: 'Initiator workspace', render: (c) => <span className="font-mono">{c.initiator.name}</span> },
            { key: 'authenticator', header: 'Authenticator workspace', render: (c) => <span className="font-mono">{c.authenticator.name}</span> },
            {
              key: 'status',
              header: 'Status',
              render: (c) => (
                <>
                  <Chip variant={STATUS_CHIP[c.status] ?? 'plain'}>{STATUS_LABEL[c.status] ?? c.status}</Chip>
                  {c.status === 'ACTIVATION_PENDING' && c.role === 'AUTHENTICATOR' && <Note>Awaiting your activation</Note>}
                </>
              ),
            },
            { key: 'created', header: 'Created date (UTC)', render: (c) => <span className="font-mono">{new Date(c.created_at).toISOString().slice(0, 10)}</span> },
            {
              key: 'activated',
              header: 'Activation date (UTC)',
              render: (c) => <span className="font-mono">{c.activated_at ? new Date(c.activated_at).toISOString().slice(0, 10) : '—'}</span>,
            },
            {
              key: 'action',
              header: 'Action',
              render: (c) => (
                <>
                  {c.status === 'ACTIVATION_PENDING' && c.role === 'AUTHENTICATOR' && (
                    <Button data-act={actionId('connection.activate')} ghost disabled={activate.isPending} onClick={() => activate.mutate(c.connection_id)}>
                      Activate
                    </Button>
                  )}
                  {c.status !== 'DEACTIVATED' && (
                    <Button data-act={actionId('connection.deactivate')} ghost disabled={deactivate.isPending} onClick={() => deactivate.mutate(c.connection_id)}>
                      Deactivate
                    </Button>
                  )}
                </>
              ),
            },
          ]}
          rows={rows}
          getRowKey={(c) => c.connection_id}
          emptyMessage={connections.isLoading ? '' : 'No connections match these filters.'}
        />
      </div>

      <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between mt-5">
        <Note>A restore into a connected workspace still shows you what it will attempt, and still writes only the objects you select.</Note>
        <Pager
          hasPrev={pager.hasPrev}
          hasNext={Boolean(connections.data?.next_cursor)}
          loading={connections.isFetching}
          onPrev={pager.goPrev}
          onNext={() => {
            if (connections.data?.next_cursor) pager.goNext(connections.data.next_cursor)
          }}
        />
      </div>
    </div>
  )
}
